import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Autocomplete,
  TextField,
  CircularProgress,
  Typography,
} from "@mui/material";
import debounce from "lodash.debounce";

// Helper function to extract text from JSX element
const getTextFromJSX = (element) => {
  if (typeof element === "string") return element;
  if (React.isValidElement(element)) {
    return React.Children.toArray(element.props.children)
      .map(getTextFromJSX)
      .join("");
  }
  return "";
};

// Helper function to get nested value
const getValue = (obj, key) => {
  return key.split(".").reduce((o, k) => o?.[k], obj);
};

const MultiKeyAutocompleteWithPagination = ({
  selectedItem,
  onDataChange,
  selectedClient,
  width = 250,
  errorMessage,
  hideLabel,
  primaryKey,
  parenthesisKey,
  secondLineKey,
  apiEndPoint,
  disabled = false,
  customLabel,
  backgroundColor,
  data = [],
  multiple = false,
  supplier,
  initSet,
}) => {
  const [allData, setAllData] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadMoreLoading, setLoadMoreLoading] = useState(false);
  const clientId = localStorage.getItem("client_id");
  const client_ref = useRef();
  const supplier_ref = useRef();
  const searchValueRef = useRef("");
  const autocompleteRef = useRef(null);

  useEffect(() => {
    if (data && data.length > 0) {
      const enriched = [...data];
      if (data.length >= 15) {
        enriched.push({ [primaryKey]: "Load More", isLoadMore: true });
      }
      setAllData(enriched);
    }
  }, [data, apiEndPoint]);

  let tim;

  const clear_data = () => {
    setAllData([]);
    setInputValue("");
    setPage(0);
    setLoading(false);
    setLoadMoreLoading(false);
    client_ref.current = null;
    supplier_ref.current = null;
    searchValueRef.current = "";
    autocompleteRef.current = null;
    tim && clearTimeout(tim);
    setLoadMoreLoading(false);
    console.log("Cleared autocomplete data");
  };

  useEffect(() => {
    client_ref.current = selectedClient?._id || clientId;
  }, [clientId, selectedClient]);

  useEffect(() => {
    if (supplier) {
      supplier_ref.current = supplier;
    } else {
      supplier_ref.current = null;
    }
  }, [supplier]);

  useEffect(() => {
    if (!initSet || typeof apiEndPoint !== "function") return;

    const initFetch = async () => {
      setLoading(true);
      try {
        const response = await apiEndPoint(
          1,
          0,
          undefined,
          client_ref.current,
          supplier_ref.current,
        );
        const items = response?.data || [];
        if (items.length > 0) {
          onDataChange?.(items[0]);
        }
      } catch (error) {
        console.error("initSet fetch failed:", error);
      } finally {
        setLoading(false);
      }
    };

    initFetch();
  }, [initSet]);

  const getCombinedLabel = (option) => {
    const primary =
      typeof primaryKey === "function"
        ? primaryKey(option)
        : getValue(option, primaryKey) || "";
    const parenthesis =
      typeof parenthesisKey === "function"
        ? parenthesisKey(option)
        : getValue(option, parenthesisKey) || "";
    const second =
      typeof secondLineKey === "function"
        ? secondLineKey(option)
        : getValue(option, secondLineKey) || "";
    let label = getTextFromJSX(primary);
    if (parenthesis) label += ` (${getTextFromJSX(parenthesis)})`;
    if (second) label += ` | ${getTextFromJSX(second)}`;
    return label;
  };

  useEffect(() => {
    if (selectedItem) {
      const label = getCombinedLabel(selectedItem);
      setInputValue(label);
      searchValueRef.current = label;
    }
  }, [selectedItem, primaryKey, parenthesisKey, secondLineKey]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        autocompleteRef.current &&
        !autocompleteRef.current.contains(event.target)
      ) {
        tim = setTimeout(() => {
          setInputValue(searchValueRef.current);
        }, 100);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      clear_data();
    };
  }, []);

  const fetchData = useCallback(
    debounce(async (searchQuery, pageNumber, isLoadMore = false) => {
      if (isLoadMore) setLoadMoreLoading(true);
      else setLoading(true);

      try {
        if (typeof apiEndPoint !== "function") {
          console.warn(
            "MultiKeyAutocompleteWithPagination: apiEndPoint is not a function",
            apiEndPoint,
          );
          if (data && data.length > 0) {
            const enriched = [...data];
            if (data.length >= 15) {
              enriched.push({ [primaryKey]: "Load More", isLoadMore: true });
            }
            setAllData(enriched);
            setPage(data.length < 15 ? -1 : 0);
            return;
          }
          setAllData([{ [primaryKey]: "No data found", isNoData: true }]);
          setPage(-1);
          return;
        }
        const limit = 15;
        const response = await apiEndPoint(
          limit,
          pageNumber * limit,
          searchQuery || undefined,
          client_ref.current,
          supplier_ref.current,
        );

        let fetched = [...(response?.data || [])];

        if (pageNumber === 0) setPage(0);

        if (fetched.length === 0) {
          setAllData([{ [primaryKey]: "No data found", isNoData: true }]);
          setPage(-1);
        } else {
          if (fetched.length === limit) {
            fetched.push({ [primaryKey]: "Load More", isLoadMore: true });
          }
          if (pageNumber === 0) {
            setAllData(fetched);
          } else {
            setAllData((prevData) => [...prevData.slice(0, -1), ...fetched]);
          }
          setPage(fetched.length < limit ? -1 : pageNumber);
        }
      } catch (error) {
        console.error("Fetch failed:", error);
      } finally {
        setLoading(false);
        setLoadMoreLoading(false);
      }
    }, 300),
    [],
  );

  const handleDataChange = (event, value) => {
    if (value?.isLoadMore || value?.isNoData) return;
    onDataChange?.(value);
  };

  const handleSearchChange = (event) => {
    const value = event.target.value;
    setInputValue(value);
    searchValueRef.current = value;
    setPage(0);
    setAllData([]);
    fetchData(value, 0);
  };

  const handleInputChange = (event, newInputValue, reason) => {
    if (reason !== "blur") {
      setInputValue(newInputValue);
      searchValueRef.current = newInputValue;
    } else {
      setTimeout(() => setInputValue(searchValueRef.current), 0);
    }
  };

  const handleLoadMoreClick = () => {
    if (page !== -1 && !loading && !loadMoreLoading) {
      const nextPage = page + 1;
      setPage(nextPage);
      fetchData(inputValue, nextPage, true);
    }
  };

  const handleTextFieldClick = () => {
    const hasRealData = allData.some(
      (item) => !item.isNoData && !item.isLoadMore,
    );
    if (!loading) {
      fetchData("", 0);
    }
  };

  return (
    <div ref={autocompleteRef}>
      <Autocomplete
        filterSelectedOptions={true}
        multiple={multiple}
        disableOpenOnFocus={false}
        forcePopupIcon
        disabled={disabled}
        size="small"
        id="data-select"
        options={allData}
        getOptionLabel={(option) => {
          if (!option) return "";
          if (option.isLoadMore) return "Load More";
          if (option.isNoData) return "No data found";
          return getCombinedLabel(option);
        }}
        value={
          selectedItem && selectedItem.isNoData ? null : selectedItem || null
        }
        inputValue={inputValue}
        onInputChange={handleInputChange}
        onFocusCapture={handleTextFieldClick}
        onChange={handleDataChange}
        filterOptions={(options) => options}
        clearOnEscape
        isOptionEqualToValue={(option, value) => {
          if (option?.isLoadMore || option?.isNoData || value?.isLoadMore)
            return false;
          const optionLabel = getCombinedLabel(option);
          const valueLabel = getCombinedLabel(value);
          return optionLabel === valueLabel;
        }}
        freeSolo
        renderOption={(props, option) => {
          const primary =
            typeof primaryKey === "function"
              ? primaryKey(option)
              : getValue(option, primaryKey) || "";
          const parenthesis =
            typeof parenthesisKey === "function"
              ? parenthesisKey(option)
              : getValue(option, parenthesisKey) || "";
          const second =
            typeof secondLineKey === "function"
              ? secondLineKey(option)
              : getValue(option, secondLineKey) || "";
          console.log(primary, parenthesis, second);

          if (option.isLoadMore) {
            return (
              <li
                {...props}
                onClick={handleLoadMoreClick}
                style={{
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  backgroundColor: "var(--bg-secondary)",
                  borderBottom: "1px solid var(--border-color)",
                  padding: "10px",
                }}
              >
                <Typography
                  color="primary"
                  variant="body2"
                  style={{ marginRight: 8 }}
                >
                  Load More...
                </Typography>
                {loadMoreLoading && (
                  <CircularProgress color="inherit" size={16} />
                )}
              </li>
            );
          }

          if (option.isNoData) {
            return (
              <li
                {...props}
                aria-disabled="true"
                style={{
                  backgroundColor: "var(--bg-secondary)",
                  borderBottom: "1px solid var(--border-color)",
                  padding: "10px",
                  cursor: "not-allowed",
                  pointerEvents: "none",
                }}
              >
                <Typography variant="body2" color="textSecondary">
                  No data found
                </Typography>
              </li>
            );
          }

          return (
            <li
              {...props}
              title={getCombinedLabel(option)}
              style={{
                backgroundColor: "var(--bg-secondary)",
                borderBottom: "1px solid var(--border-color)",
                padding: "10px",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <p
                style={{
                  display: "block",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  margin: 0,
                  width: "100%",
                  fontSize: "14px",
                  fontWeight: 500,
                }}
              >
                <Typography display={"flex"} fontSize={14}>
                  {primary}
                  {parenthesis && (
                    <Typography
                      fontSize={12}
                      ml={1}
                      color="var(--text-secondary)"
                    >
                      {parenthesis}
                    </Typography>
                  )}
                </Typography>
              </p>
              {second && (
                <p
                  style={{
                    display: "block",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    margin: "4px 0 0 0",
                    width: "100%",
                    fontSize: "12px",
                    color: "var(--text-secondary)",
                  }}
                >
                  {second}
                </p>
              )}
            </li>
          );
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            style={{ backgroundColor, borderRadius: 10 }}
            margin="dense"
            label={hideLabel ? "" : `Select ${customLabel || "data"}`}
            variant="outlined"
            onChange={handleSearchChange}
            onBlur={() =>
              setTimeout(() => setInputValue(searchValueRef.current), 0)
            }
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? (
                    <CircularProgress color="inherit" size={20} />
                  ) : null}
                  {params.InputProps.endAdornment}
                </>
              ),
              onClick: handleTextFieldClick,
            }}
            error={!!errorMessage}
            helperText={errorMessage}
            onClick={handleTextFieldClick}
          />
        )}
        style={{ width }}
      />
    </div>
  );
};

export default MultiKeyAutocompleteWithPagination;
